// src/components/ResourceEdit.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo, FaInfoCircle } from 'react-icons/fa';
import useResourceTypes from '../hooks/useResourceTypes';
import '../styles/ResourceEdit.css';
import { API_ENDPOINTS, apiRequest } from '../config/api';
import { T, Var } from '../lib/gt-shim';


const ResourceEdit = ({ resourceId }) => {
  const isCreateMode = !resourceId;
  const [resource, setResource] = useState(isCreateMode ? {} : null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    website: '',
    email: '',
    hours: '',
    resource_type_id: 1,
    accepts_uninsured: false,
    sliding_scale: false,
    free_care_available: false,
    notes: '',
    eligibility_criteria: ''
  });
  const [isLoading, setIsLoading] = useState(!isCreateMode);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const navigate = useNavigate();

  // Use our custom hook to get resource types
  const { resourceTypes, isLoading: isLoadingTypes } = useResourceTypes();

  // Fetch the resource data when the component mounts or resourceId changes
  useEffect(() => {
    if (isCreateMode) return;
    const fetchResource = async () => {
      if (!resourceId) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await apiRequest(API_ENDPOINTS.RESOURCES.BY_ID(resourceId));
        setResource(data);

        // Initialize form data with resource data
        setFormData({
          name: data.name || '',
          display_name: data.display_name || data.name || '',
          address_line1: data.address_line1 || '',
          address_line2: data.address_line2 || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
          phone: data.phone || '',
          website: data.website || '',
          email: data.email || '',
          hours: data.hours || '',
          resource_type_id: data.resource_type_id || 1,
          accepts_uninsured: data.accepts_uninsured || false,
          sliding_scale: data.sliding_scale || false,
          free_care_available: data.free_care_available || false,
          notes: data.notes || '',
          eligibility_criteria: data.eligibility_criteria || ''
        });
      } catch (err) {
        console.error('Error fetching resource:', err);
        setError('Failed to load resource information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchResource();
  }, [resourceId, isCreateMode]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // For checkboxes, use checked property; otherwise, use value
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      let result;
      if (isCreateMode) {
        result = await apiRequest(API_ENDPOINTS.RESOURCES.BASE, {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        setSuccessMessage('Resource added successfully');
        navigate('/admin/resources', { state: { fromEdit: true } });
      } else {
        result = await apiRequest(API_ENDPOINTS.RESOURCES.BY_ID(resourceId), {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        setResource(result);
        setSuccessMessage('Resource updated successfully');
        navigate('/admin/resources', { state: { fromEdit: true } });
      }
    } catch (err) {
      setError('Failed to ' + (isCreateMode ? 'add' : 'update') + ' resource');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    // Reset form to original resource data
    if (resource) {
      setFormData({
        name: resource.name || '',
        display_name: resource.display_name || resource.name || '',
        address_line1: resource.address_line1 || '',
        address_line2: resource.address_line2 || '',
        city: resource.city || '',
        state: resource.state || '',
        zip: resource.zip || '',
        phone: resource.phone || '',
        website: resource.website || '',
        email: resource.email || '',
        hours: resource.hours || '',
        resource_type_id: resource.resource_type_id || 1,
        accepts_uninsured: resource.accepts_uninsured || false,
        sliding_scale: resource.sliding_scale || false,
        free_care_available: resource.free_care_available || false,
        notes: resource.notes || '',
        eligibility_criteria: resource.eligibility_criteria || ''
      });
    }
  };

  if (!isCreateMode && isLoading || isLoadingTypes) {
    return <T id="components.resourceedit.0"><div className="resource-edit-loading">Loading resource information...</div></T>;
  }

  if (error) {
    return (<T id="components.resourceedit.1">
      <div className="resource-edit-error">
        <FaInfoCircle />
        <p><Var>{error}</Var></p>
        <button onClick={() => navigate('/admin')} className="btn btn-primary">
          Back to Admin
        </button>
      </div></T>
    );
  }

  if (!isCreateMode && !resource) {
    return (<T id="components.resourceedit.2">
      <div className="resource-edit-error">
        <FaInfoCircle />
        <p>Resource not found</p>
        <button onClick={() => navigate('/admin')} className="btn btn-primary">
          Back to Admin
        </button>
      </div></T>
    );
  }

  return (
    <div className="resource-edit">
      <h2>
        {isCreateMode ? 'Add Resource' : <>Edit Resource: {resource?.name || ''}</>}
      </h2>
      {successMessage && (
        <div className="success-message">
          <FaInfoCircle />
          <p>{successMessage}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="edit-form">
        <div className="form-section">
          <h3>Basic Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Original Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required />
              
            </div>
            
            <div className="form-group">
              <label htmlFor="display_name">Display Name</label>
              <input
                type="text"
                id="display_name"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                required />
              
              <small>This is the name shown to users</small>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="resource_type_id">Resource Type</label>
              <select
                id="resource_type_id"
                name="resource_type_id"
                value={formData.resource_type_id}
                onChange={handleInputChange}
                required>
                
                <Var>{resourceTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                  ))}</Var>
              </select>
            </div>
          </div>

          {/* Address and Phone Fields */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address_line1">Address Line 1</label>
              <input
                type="text"
                id="address_line1"
                name="address_line1"
                value={formData.address_line1}
                onChange={handleInputChange}
                required />
              
            </div>
            <div className="form-group">
              <label htmlFor="address_line2">Address Line 2</label>
              <input
                type="text"
                id="address_line2"
                name="address_line2"
                value={formData.address_line2}
                onChange={handleInputChange} />
              
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required />
              
            </div>
            <div className="form-group">
              <label htmlFor="state">State</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                required
                maxLength={2} />
              
            </div>
            <div className="form-group">
              <label htmlFor="zip">ZIP</label>
              <input
                type="text"
                id="zip"
                name="zip"
                value={formData.zip}
                onChange={handleInputChange}
                required
                maxLength={10} />
              
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://example.com" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="accepts_uninsured"
                  checked={formData.accepts_uninsured}
                  onChange={handleInputChange} />
                
                Accepts Uninsured
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="sliding_scale"
                  checked={formData.sliding_scale}
                  onChange={handleInputChange} />
                
                Sliding Scale
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="free_care_available"
                  checked={formData.free_care_available}
                  onChange={handleInputChange} />
                
                Free Care Available
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Enter any additional details or notes for this resource" />
              
            </div>
          </div>
        </div>
        
        {/* Rest of your form... */}
        
        <div className="form-actions">
          {!isCreateMode && (
            <button
              type="button"
              onClick={handleReset}
              className="btn btn-outline"
              disabled={isLoading}>
              <FaUndo /> Reset Changes
            </button>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}>
            {isLoading
              ? (isCreateMode ? 'Adding...' : 'Saving...')
              : <><FaSave /> {isCreateMode ? 'Add Resource' : 'Save Changes'}</>
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResourceEdit;